import {
  BoundMultiSelectField,
  BoundSelectField,
  Button,
  Css,
  FormLines,
  Icon,
  IconButton,
  Palette,
  ScrollableParent,
  Step,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { useCallback } from "react";
import { StepActions, StepperProvider, useStepperContext } from "src/components/stepper";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { CostType, Maybe, SaveBidPackageInput } from "src/generated/graphqlTypes";
import { queryResult } from "src/utils";
import {
  BidPackageDetailPageDocument,
  PageBidPackageDetailFragment,
  useBidPackageDetailPageQuery,
} from "../BidPackageDetailPage.generated";
import { EditBidPackageLineItemModal } from "../add-bid-package/components/BidPackageLineItemModal";
import { BidScopeFields } from "../add-bid-package/components/BidScopeFields";
import { useSaveBidPackageMutation } from "../publish-bid-package/PublishBidPackagePage.generated";
import {
  EditBidPackageDetailsStep_BidScopeSourceFragment,
  useEditBidPackageDetailsStepQuery,
} from "./EditBidPackageModal.generated";

type EditBidPackageModalProps = { bidPackage: PageBidPackageDetailFragment };

const initialSteps: Step[] = [
  { label: "Details", state: "incomplete", value: "details" },
  { label: "Line Items", state: "incomplete", value: "line-items" },
];

export function EditBidPackageModal({ bidPackage }: EditBidPackageModalProps) {
  // Since we can change the cost codes and cost type, we need this to pull the freshest data from the cache
  const { data } = useBidPackageDetailPageQuery({ variables: { id: bidPackage.id }, fetchPolicy: "cache-only" });
  return (
    <StepperProvider steps={initialSteps} newLayout currentStep={initialSteps[1]}>
      <StepContent bidPackage={data?.bidPackage ?? bidPackage} />
    </StepperProvider>
  );
}

function StepContent({ bidPackage }: { bidPackage: PageBidPackageDetailFragment }) {
  const { currentStep } = useStepperContext();

  switch (currentStep.value) {
    case "details":
      return <DetailsStep bidPackage={bidPackage} />;
    case "line-items":
      return <EditBidPackageLineItemModal bidPackage={bidPackage} />;
    default:
      return null;
  }
}

function DetailsStep({ bidPackage }: { bidPackage: PageBidPackageDetailFragment }) {
  const query = useEditBidPackageDetailsStepQuery();
  return queryResult(query, (data) => <DetailsStepView bidPackage={bidPackage} {...data} />);
}

type DetailsStepViewProps = {
  bidPackage: PageBidPackageDetailFragment;
  markets: NamedFragment[];
  bidScopeSourceDetail: EditBidPackageDetailsStep_BidScopeSourceFragment[];
  costCodes: NamedFragment[];
};

function DetailsStepView({ bidPackage, markets, bidScopeSourceDetail, costCodes }: DetailsStepViewProps) {
  const [saveBidPackage] = useSaveBidPackageMutation({ refetchQueries: [BidPackageDetailPageDocument] });
  const { closeModal } = useModal();
  const { nextStep } = useStepperContext();
  const formState = useFormState({
    config: formConfig,
    init: {
      input: bidPackage,
      map: (bp) => ({
        id: bp.id,
        marketId: bp.projects?.first?.market?.id,
        projects: bp.projects?.map((p) => p.id),
        scopeSource: bp.scopeSource?.code,
        costCodes: bp.costCodes?.map((cc) => cc.id),
        costType: bp.costType?.code,
      }),
    },
  });
  const save = useCallback(
    async (exit: boolean) => {
      const { projects, marketId, scopeSource, id, costCodes, costType } = formState.value;
      await saveBidPackage({
        variables: {
          input: {
            id,
            scopeSource,
            // Both market-level and community-level bid packages use the projects field
            projects: marketId && (projects ?? []).isEmpty ? [marketId] : (projects ?? []),
            costCodes,
            costType,
          },
        },
      });
      if (exit) closeModal();
      else nextStep();
    },
    [formState, saveBidPackage],
  );

  return (
    <ScrollableParent xss={Css.h("calc(100% - 144px)").$}>
      <header data-testid="bidLineItemsModal" css={Css.pb4.pt2.bb.bw1.bcGray200.bgWhite.$}>
        <div css={Css.df.fdc.$}>
          <div css={Css.ml("97%").$}>
            <IconButton icon="x" onClick={closeModal} />
          </div>
          <div css={Css.df.fdc.aic.jcc.$}>
            <h1 css={Css.xl2.$}>{bidPackage.name}</h1>
          </div>
        </div>
      </header>
      <div css={Css.df.gap3.mt2.aic.jcc.$}>
        <div css={Css.w50.p4.bgWhite.bshBasic.br12.mb3.$}>
          <div css={Css.lg.mb2.$}>Package Overview</div>
          <FormLines labelStyle="left" width="full">
            <BidScopeFields
              formState={formState}
              markets={markets}
              scopeSources={bidScopeSourceDetail}
              disabled={{ market: true, scopeSource: true, projects: !bidPackage.isUnitBased }}
            />
            <div css={Css.df.gap1.gray600.sm.$}>
              <Icon icon="infoCircle" color={Palette.Gray600} />
              If there are any newly relevant product offerings as a result of changing the bid, bid package updates
              will be created for them
            </div>
            <BoundMultiSelectField
              field={formState.costCodes}
              options={costCodes}
              multiline
              // Disallow removing cost codes that are already in the bid package
              disabledOptions={bidPackage.costCodes?.map((cc) => cc.id)}
            />
            <BoundSelectField
              field={formState.costType}
              getOptionValue={(ct) => ct.code}
              getOptionLabel={(ct) => ct.name}
              disabledOptions={
                // cannot change from material to labor or vice versa, or change subcontractor to labor or material
                bidPackage.costType?.code === CostType.Materials
                  ? [CostType.Labor]
                  : bidPackage.costType?.code === CostType.Labor
                    ? [CostType.Materials]
                    : [CostType.Labor, CostType.Materials]
              }
              options={[
                { code: CostType.Labor, name: "Labor" },
                { code: CostType.Materials, name: "Material" },
                { code: CostType.Subcontractor, name: "Subcontractor" },
              ]}
            />
          </FormLines>
        </div>
      </div>
      <StepActions>
        <>
          <Button variant="secondary" label="Save & Exit" size="lg" onClick={() => save(true)} />
          <Button label="Save" onClick={() => save(false)} size="lg" />
        </>
      </StepActions>
    </ScrollableParent>
  );
}

export type EditBidPackageFormState = Pick<
  SaveBidPackageInput,
  "projects" | "scopeSource" | "id" | "costCodes" | "costType"
> & { marketId: Maybe<string> };

const formConfig: ObjectConfig<EditBidPackageFormState> = {
  id: { type: "value", rules: [required] },
  marketId: { type: "value", rules: [required] },
  projects: { type: "value" },
  scopeSource: { type: "value", rules: [required] },
  costCodes: { type: "value", rules: [required] },
  costType: { type: "value", rules: [required] },
};
